import { inject, Injectable } from '@angular/core';
import Pusher, { Channel } from 'pusher-js';
import { environment } from 'src/environments/environment';
import { User } from '../models/user.model';
import { TokenService } from './token.service';
import { ClockRegisterService } from './clock-register.service';
import { EmployeeService } from './employee.service';
import { ClockRegisterStatus } from '../models/clock-register.model';
import { LongTaskPusherSerialized } from '../models/long_task_pusher_serialized.model';
import { ActionsRequired } from '../models/actions-required.interface';
import { MeetingRecordMediaRecordingPusherUpdate } from '../models/meeting-record-media-recording-pusher-update.model';
import { MeetingRecordSummaryGenerationPusherUpdate } from '../models/meeting-record-summary-generation-pusher-update.model';

interface MeetingRecordChannelListeners {
  media_recording_updated_handlers: Set<(data: MeetingRecordMediaRecordingPusherUpdate) => void>;
  summary_generation_updated_handlers: Set<(data: MeetingRecordSummaryGenerationPusherUpdate) => void>;
}

interface SubscribedChannelEntry {
  channel: Channel;
  meeting_record_listeners: MeetingRecordChannelListeners | null;
}

@Injectable({
  providedIn: 'root',
})
export class PusherService {
  private pusher!: Pusher;
  private channel_name = 'private-user.';
  private subscribed_channels: Map<string, SubscribedChannelEntry> = new Map();

  private readonly token = inject(TokenService);
  private readonly clock = inject(ClockRegisterService);
  private readonly employee = inject(EmployeeService);

  initUser(user: User) {
    this.channel_name += user.id.toString();

    this.initPusher();

    const channel = this.pusher.subscribe(this.channel_name);

    // Clock
    channel.bind('App\\Events\\ClockChangeEvent', (data: ClockRegisterStatus) => {
      this.clock.onPusherClockEventReceived(data);
    });

    // Actions Required
    channel.bind('App\\Events\\ActionsRequiredUpdatedEvent', (data: ActionsRequired): void => {
      this.employee.onActionsRequiredUpdated(data);
    });
  }

  unsubscribeFromPusher() {
    if (this.pusher) {
      this.pusher.unsubscribe(this.channel_name);

      // Tornem la variable al seu valor inicial
      this.channel_name = 'private-user.';
    }
  }

  /**
   * Subscribe to frontend version updates channel and listen for version update events
   * @param callback Function to call when a new version is available
   * @returns The channel instance
   */
  subscribeToFrontendVersionUpdatesChannel(callback: (data: { version: string; is_production: boolean }) => void): Channel {
    if (!this.pusher) {
      this.initPusher();
    }

    const channel_name = 'private-frontend-version-updates';
    const channel = this.pusher.subscribe(channel_name);
    this.subscribed_channels.set(channel_name, { channel, meeting_record_listeners: null });

    // Listen for FrontendVersionUpdateRequiredEvent
    channel.bind('App\\Events\\FrontendVersionUpdateRequiredEvent', (data: { version: string; is_production: boolean }) => {
      callback(data);
    });

    return channel;
  }

  /**
   * Subscribe to a dynamic channel and listen for LongTask updates
   * @param channel_name The channel name to subscribe to
   * @param callback Function to call when the task status updates
   * @returns The channel instance
   */
  subscribeToLongTaskChannel(channel_name: string, callback: (data: LongTaskPusherSerialized) => void): Channel {
    if (!this.pusher) {
      this.initPusher();
    }

    const channel = this.pusher.subscribe(channel_name);
    this.subscribed_channels.set(channel_name, { channel, meeting_record_listeners: null });

    channel.bind('App\\Events\\LongTaskCompletedEvent', callback);

    return channel;
  }

  /**
   * Bind to LongTask progress events on an already-subscribed channel (call after subscribeToLongTaskChannel).
   */
  subscribeToLongTaskProgress(channel_name: string, callback: (data: { progress?: number; message?: string }) => void): void {
    const subscribed_channel = this.subscribed_channels.get(channel_name);

    if (subscribed_channel) {
      subscribed_channel.channel.bind('App\\Events\\LongTaskProgressEvent', callback);
    }
  }

  subscribeToMeetingRecordChannel(
    meeting_record_id: number,
    on_media_recording_updated: (data: MeetingRecordMediaRecordingPusherUpdate) => void,
    on_summary_generation_updated?: (data: MeetingRecordSummaryGenerationPusherUpdate) => void
  ): () => void {
    if (!this.pusher) {
      this.initPusher();
    }

    const channel_name = `private-meeting-record.${meeting_record_id}`;
    let subscribed_channel = this.subscribed_channels.get(channel_name);

    if (subscribed_channel === undefined) {
      const channel = this.pusher.subscribe(channel_name);
      const meeting_record_listeners: MeetingRecordChannelListeners = {
        media_recording_updated_handlers: new Set(),
        summary_generation_updated_handlers: new Set(),
      };

      channel.bind('App\\Events\\MeetingRecordMediaRecordingUpdatedEvent', (data: MeetingRecordMediaRecordingPusherUpdate) => {
        meeting_record_listeners.media_recording_updated_handlers.forEach(handler => handler(data));
      });

      channel.bind('App\\Events\\MeetingRecordSummaryGenerationUpdatedEvent', (data: MeetingRecordSummaryGenerationPusherUpdate) => {
        meeting_record_listeners.summary_generation_updated_handlers.forEach(handler => handler(data));
      });

      subscribed_channel = { channel, meeting_record_listeners };
      this.subscribed_channels.set(channel_name, subscribed_channel);
    }

    const meeting_record_listeners = subscribed_channel.meeting_record_listeners;

    if (meeting_record_listeners === null) {
      throw new Error(`El canal ${channel_name} ya está suscrito sin listeners de acta.`);
    }

    meeting_record_listeners.media_recording_updated_handlers.add(on_media_recording_updated);

    if (on_summary_generation_updated !== undefined) {
      meeting_record_listeners.summary_generation_updated_handlers.add(on_summary_generation_updated);
    }

    return () => {
      this.removeMeetingRecordChannelListener(channel_name, on_media_recording_updated, on_summary_generation_updated);
    };
  }

  /**
   * Unsubscribe from a specific channel
   * @param channel_name The channel name to unsubscribe from
   */
  unsubscribeFromChannel(channel_name: string): void {
    this.teardownSubscribedChannel(channel_name);
  }

  /**
   * Unsubscribe from all dynamic channels
   */
  unsubscribeFromAllDynamicChannels(): void {
    [...this.subscribed_channels.keys()].forEach(channel_name => {
      this.teardownSubscribedChannel(channel_name);
    });
  }

  private removeMeetingRecordChannelListener(
    channel_name: string,
    on_media_recording_updated: (data: MeetingRecordMediaRecordingPusherUpdate) => void,
    on_summary_generation_updated?: (data: MeetingRecordSummaryGenerationPusherUpdate) => void
  ): void {
    const subscribed_channel = this.subscribed_channels.get(channel_name);
    const meeting_record_listeners = subscribed_channel?.meeting_record_listeners;

    if (meeting_record_listeners === undefined || meeting_record_listeners === null) {
      return;
    }

    meeting_record_listeners.media_recording_updated_handlers.delete(on_media_recording_updated);

    if (on_summary_generation_updated !== undefined) {
      meeting_record_listeners.summary_generation_updated_handlers.delete(on_summary_generation_updated);
    }

    const has_remaining_handlers =
      meeting_record_listeners.media_recording_updated_handlers.size > 0 ||
      meeting_record_listeners.summary_generation_updated_handlers.size > 0;

    if (!has_remaining_handlers) {
      this.teardownSubscribedChannel(channel_name);
    }
  }

  private teardownSubscribedChannel(channel_name: string): void {
    if (!this.subscribed_channels.has(channel_name)) {
      return;
    }

    if (this.pusher) {
      this.pusher.unsubscribe(channel_name);
    }

    this.subscribed_channels.delete(channel_name);
  }

  private initPusher() {
    this.pusher = new Pusher(environment.pusher.key, {
      cluster: environment.pusher.cluster,
      authEndpoint: environment.laravel_url + '/api/broadcasting/auth',
      auth: {
        headers: {
          Authorization: `Bearer ${this.token.get()}`,
        },
      },
    });
  }
}
